import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { observer } from 'mobx-react';

import { stripTags } from 'apps/utils';
import { domainURL } from 'core/url';
import { conf as globalConf } from 'apps/config.yaml';
import { routerStore } from 'core/routing';
import { inWhitelabel, getWhitelabelConfig } from 'core/whitelabel';

const { siteName } = globalConf;

const getRobotsTagContent = value => {
  if (value === 'noindex') return 'noindex, nofollow';

  return 'index, follow';
};

const DocumentHead = observer(
  ({
    title = null,
    description = null,
    imagePath = null,
    videoInfo = null,
    ogType = 'website',
    authorTwitter = null,
    index = 'index',
    canonicalPath = null,
    ogTypeParams = {},
    notificationCount = undefined,
  } = {}) => {
    /*
  TODO: iso861 dates

  https://developers.facebook.com/docs/reference/opengraph
  if og_type=='profile' ogTypeParams should be {first_name, last_name, username}
  if og_type=='article' ogTypeParams should be {
    author (link to author profile om wf),
    publisher: 'https://facebook.com/wikifactory'
    published_time: iso8601
    modified_time: iso8601
    section: str
    tag: str

  FOR GOOGLE
  <script type="application/ld+json">
  {"@context":"http://schema.org","@type":"NewsArticle","image":{"@type":"ImageObject","width":1920,"height":1280,"url":"https://cdn-images-1.medium.com/max/2000/1*J8-dxYhUWEV959Tx6A2BYQ.jpeg"},"url":"","dateCreated":"2017-12-08T14:52:39.915Z","datePublished":"2017-12-08T14:52:39.915Z","dateModified":"2018-08-16T22:11:05.081Z","headline":"How Aretha Franklin Earned 'Respect' by Flipping Gender Roles","name":"How Aretha Franklin Earned 'Respect' by Flipping Gender Roles","thumbnailUrl":"https://cdn-images-1.medium.com/max/2000/1*J8-dxYhUWEV959Tx6A2BYQ.jpeg","keywords":["Tag:Music","Tag:History","Tag:Rhythm And Blues","Tag:Gender Equality","Topic:Featured for members","Topic:Culture","Topic:Music","Topic:Featured stories","LockedPostSource:1","Elevated:true","LayerCake:2"],"author":{"@type":"Person","name":"Steve Chatterton","url":"https://medium.com/@stevechatterton"},"creator":["Steve Chatterton"],"publisher":{"@type":"Organization","name":"Medium","url":"https://medium.com/","logo":{"@type":"ImageObject","width":308,"height":60,"url":"https://cdn-images-1.medium.com/max/616/1*OMF3fSqH8t4xBJ9-6oZDZw.png"}}}
  </script>

  <link rel='dns-prefetch' href='hotjar/intercom/analytics/cloudinary/s3' />
  -- main index.html?? (don't wait for js to load)
  } */
    // WARNING: since we don't change the underlying 'route' but just update
    // route.name and route.params, the routerStore.route.path will not be correct
    // i.e. don't do this: const path = routerStore.route.path;
    const { path } = routerStore;
    if (description) {
      description = stripTags(description);
      if (description.length > 297) {
        description = `${description.slice(0, 297)}...`;
      }
    }
    if (canonicalPath === null) {
      canonicalPath = path;
    }
    let ogTypeTags;
    if (ogType === 'website') {
      ogTypeTags = [<meta key='og:type' property='og:type' content={ogType} />];
    } else if (ogType === 'profile') {
      ogTypeTags = [<meta key='og:type' property='og:type' content={ogType} />];
      // eslint-disable-next-line
      for (const key of Object.keys(ogTypeParams)) {
        /* eslint-disable no-template-curly-in-string */
        ogTypeTags.push(
          <meta
            key={`${ogType}:${key}`}
            property={`${ogType}:${key}`}
            content={ogTypeParams[key]}
          />,
        );
      }
    }

    let currentSite = siteName;
    let domainTwitter = 'wikifactory.com';
    // Use hook until https://github.com/nfl/react-helmet/issues/430 is fixed
    // Workarounds proposed in issue don't currently work for us
    useEffect(() => {
      if (notificationCount === undefined) return;
      const faviconElement = document.getElementById('favicon');
      const favicon =
        notificationCount > 0 ? '/favicon-alert.ico' : '/favicon.ico';
      // Bang cache with timestamp
      if (faviconElement) {
        faviconElement.href = `${favicon}?t=${+new Date()}`;
      }
    }, [notificationCount]);
    if (inWhitelabel()) {
      const wConfig = getWhitelabelConfig();
      currentSite = `${wConfig.title} | ${siteName}`;
      domainTwitter = wConfig.host;
    }

    return (
      <Helmet>
        <meta property='og:site_name' content={currentSite} />
        {title
          ? [
              <title key='title'>{`${title} | ${siteName}`}</title>,
              <meta key='og:title' property='og:title' content={title} />,
              <meta key='twitter:title' name='twitter:title' content={title} />,
            ]
          : ''}
        {ogTypeTags}
        {description
          ? [
              <meta
                key='description'
                name='description'
                content={description}
              />,
              <meta
                key='og:description'
                property='og:description'
                content={description}
              />,
              <meta
                key='twitter:description'
                name='twitter:description'
                content={description}
              />,
            ]
          : ''}
        {path
          ? [
              <meta key='og:url' property='og:url' content={domainURL(path)} />,
              <link
                key='canonical'
                rel='canonical'
                href={domainURL(canonicalPath)}
              />,
            ]
          : ''}
        {videoInfo
          ? [
              <meta
                key='og:video'
                property='og:video'
                content={videoInfo.url}
              />,
              <meta
                key='og:video:url'
                property='og:video:url'
                content={videoInfo.url}
              />,
              <meta
                key='og:video:secure_url'
                property='og:video:secure_url'
                content={videoInfo.url}
              />,
              <meta
                key='og:video:type'
                property='og:video:type'
                content='video/mp4'
              />,
              <meta
                key='og:video:width'
                property='og:video:width'
                content={videoInfo.width}
              />,
              <meta
                key='og:video:height'
                property='og:video:height'
                content={videoInfo.height}
              />,
            ]
          : null}
        {imagePath
          ? [
              <meta key='og:image' property='og:image' content={imagePath} />,
              <meta
                key='twitter:image'
                name='twitter:image'
                content={imagePath}
              />,
              <meta
                key='twitter:card'
                name='twitter:card'
                content='summary_large_image'
              />,
            ]
          : [<meta key='twitter:card' name='twitter:card' content='summary' />]}
        <meta name='robots' content={getRobotsTagContent(index)} />
        <meta name='twitter:site' content='@wikifactory' />
        <meta name='twitter:domain' content={domainTwitter} />
        {authorTwitter && (
          <meta name='twitter:creator' content={authorTwitter} />
        )}
        )
      </Helmet>
    );
  },
);

/*
Also consider og:video/audio tags - for accompanying video. All to set this when
embedding video in readme etc.

<meta property="og:locale" content="en_GB" />
<meta property="og:locale:alternate" content="fr_FR" />

Images:
<meta property="og:image:secure_url" content="https://secure.example.com/ogp.jpg" />
<meta property="og:image:type" content="image/jpeg" />
<meta property="og:image:width" content="400" />
<meta property="og:image:height" content="300" />
<meta property="og:image:alt" content="A shiny red apple with a bite taken out" />

Also consider adding og:product and og:place.
al:ios:app_name AppLinks e.g. android/ios app links from content on FB: https://developers.facebook.com/docs/applinks/metadata-reference
apple-touch tags?
Finally for 3d we can use gltf to preview in FB: https://developers.facebook.com/docs/sharing/3d-posts/open-graph-sharing
*/

DocumentHead.propTypes = {
  title: PropTypes.string,
};

export default DocumentHead;
